import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Components from './components';
import { CustomElementObservedAttributes } from './models/customElements';
import { customElementAttributeMappers } from './lib/customElements';

// Setup mutation observer for Radix popper elements
function setupRadixPopperObserver() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement && (
          node.getAttribute('data-radix-popper-content-wrapper') !== null
          || node.getAttribute('data-radix-focus-guard') !== null
          || (node.getAttribute('role') === 'dialog' && node.id.startsWith('radix-'))
        ) && !node.shadowRoot) {
          node.setAttribute('data-raes-ui', '');
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// export default function () {
// Set up observer for Radix popper elements
setupRadixPopperObserver();

// Helper function to wrap React components as web components
function createWebComponent(
  name: string,
  Component: React.ComponentType<Record<string, unknown>>,
  _customElementObservedAttributes?: CustomElementObservedAttributes
) {
  const customElementObservedAttributes: CustomElementObservedAttributes = {
    ..._customElementObservedAttributes,
    ['data-id']: customElementAttributeMappers.id
  };

  class WebComponent extends HTMLElement {
    private root: ReactDOM.Root | null = null;
    private initialChildren: Node[] = [];
    private hasSimpleText: boolean = false;
    private storedText: string | null = null;

    constructor() {
      super();
      this.setAttribute('data-raes-ui', '');
      if (this.id) {
        this.setAttribute('data-id', this.id);
        this.id = '';
      }
    }

    static get observedAttributes() {
      return _customElementObservedAttributes ? Object.keys(_customElementObservedAttributes) : undefined;
    }

    private prepareRender() {
      if (!this.hasSimpleText) {
        // Only clear and restore children for components that need HTML parsing
        this.innerHTML = '';
        if (this.initialChildren.length > 0) {
          this.initialChildren.forEach(node => {
            this.appendChild(node.cloneNode(true));
          });
        }
      }
    }

    private renderComponent() {
      const props: Record<string, unknown> = {
        ...this.getProps(),
        _domElement: this, // Pass web component element reference
      };

      // If we had text content, pass it directly
      if (this.hasSimpleText && this.storedText !== null) {
        props.children = this.storedText;
      }

      return React.createElement(Component, props);
    }

    connectedCallback() {
      // Store initial children state
      this.initialChildren = Array.from(this.childNodes);

      // Check if we have simple text content
      if (this.initialChildren.length === 1 && this.initialChildren[0].nodeType === Node.TEXT_NODE) {
        this.hasSimpleText = true;
        this.storedText = this.initialChildren[0].textContent;
      }

      // Prepare for rendering
      this.prepareRender();

      this.root = ReactDOM.createRoot(this);

      // Let React take over rendering
      this.root.render(this.renderComponent());
      this.style.display = 'contents';
    }

    disconnectedCallback() {
      if (this.root) {
        this.root.unmount();
      }
    }

    attributeChangedCallback() {
      if (this.root) {
        // Restore children before re-rendering
        this.prepareRender();
        this.root = ReactDOM.createRoot(this);
        this.root.render(this.renderComponent());
        this.style.display = 'contents';
      }
    }

    private getProps() {
      const props: Record<string, unknown> = {};

      for (const attr of this.attributes) {
        const value = attr.value;

        const mapper = customElementObservedAttributes[attr.name];
        const mappedAttribute = mapper?.(value, this);

        if (mappedAttribute) {
          props[mappedAttribute.prop || attr.name] = mappedAttribute.value;
        } else {
          props[attr.name] = value;
        }
      }

      return props;
    }
  }

  customElements.define(name, WebComponent);
}

Object.keys(Components).forEach(componentName => {
  const component = Components[componentName as keyof typeof Components];
  const tag = component?.prototype?.customElementTag;
  const customElementObservedAttributes = component?.prototype?.customElementObservedAttributes;
  if (tag) {
    createWebComponent(tag, component as React.ComponentType<Record<string, unknown>>, customElementObservedAttributes || []);
  }
})